import { generateClient } from "aws-amplify/api";
import { campaignsByTenant, listPaymentProfiles, listSuppressions, manualProfileByTenant, profileByTenant, segmentByTenant } from "../graphql/queries";


export async function checkSegmentUsage(segmentId, tenantId) {
    const tenantCampaigns = await getTenantCampaigns(tenantId);

    // Check if any of the tenant's campaigns are using the segment
    const isSegmentInUse = tenantCampaigns.some(campaign => {
        return campaign.segments && JSON.parse(campaign.segments).some(segment => segment.segmentId === segmentId);
    });

    if (isSegmentInUse) {
        return {
            inUse: true,
            message: 'This segment is currently in use by an active or paused campaign and cannot be deleted.'
        }
    }

    const manualProfiles = await tenantGraphqlRequest(manualProfileByTenant, tenantId);
    const isSegmentUsedInManualDial = manualProfiles.manualProfileByTenant.items.some(profile => profile.segment === segmentId);

    if (isSegmentUsedInManualDial) {
        return {
            inUse: true,
            message: 'This segment is currently in use by a manual dialing profile and cannot be deleted.'
        }
    }

    return { inUse: false };
}

export async function checkCommunicationProfileUsage(profileId, tenantId) {
    const tenantCampaigns = await getTenantCampaigns(tenantId);
    const usedBy = '';
    // Check if any of the tenant's campaigns are using the communication profile
    const isProfileInUse = tenantCampaigns.some(campaign => {
        return campaign.campaignProfileId === profileId;
    });

    if (isProfileInUse) {
        return {
            inUse: true,
            message: 'This communication profile is currently in use by an active or paused campaign and cannot be deleted.'
        }
    }

    const manualProfiles = await tenantGraphqlRequest(manualProfileByTenant, tenantId);
    const isProfileUsedInManualDial = manualProfiles.manualProfileByTenant.items.some(profile => profile.dialingProfile === profileId);

    if (isProfileUsedInManualDial) {
        return {
            inUse: true,
            message: 'This communication profile is currently in use by a manual dialing profile and cannot be deleted.'
        }
    }

    return { inUse: false };
}

export async function checkMessageTemplateUsage(templateId, tenantId) {
    const digitalProfiles = await tenantGraphqlRequest(listPaymentProfiles, tenantId);

    // Check if any of the tenant's digital channels are using the message template
    let isTemplateInUse = false;
    for (const profile of digitalProfiles.listPaymentProfiles.items) {
        if (JSON.parse(profile.channels.email).messsageTemplate === templateId || JSON.parse(profile.channels.sms).messsageTemplate === templateId) {
            isTemplateInUse = true;
            break;
        }
    }

    if (isTemplateInUse) {
        return {
            inUse: true,
            message: 'This message template is currently in use in a Digital Profile and cannot be deleted.'
        }
    }

    return { inUse: false };
}

export async function checkPaymentProfileUsage(profileId, tenantId) {
    const tenantProfiles = await tenantGraphqlRequest(profileByTenant, tenantId);

    // Check if any of the tenant's digital channels are using the payment profile
    const isDigitalProfileInUse = tenantProfiles.profileByTenant.items.some(profile => {
        return profile.channels && profile.channels.some(channel => channel.paymentProfileId === profileId);
    });

    if (isDigitalProfileInUse) {
        return {
            inUse: true,
            message: 'This payment profile is currently in use in a Communication Profile and cannot be deleted.'
        }
    }

    return { inUse: false };
}

export async function checkSuppressionProfileUsage(profileId, tenantId) {
    const tenantSegments = await tenantGraphqlRequest(segmentByTenant, tenantId);

    // Check if any of the tenant's segments are using the suppression profile
    const isSegmentInUse = tenantSegments.segmentByTenant.items.some(segment => {
        return segment.suppressionProfileId === profileId;
    });

    if (isSegmentInUse) {
        return {
            inUse: true,
            message: 'This suppression profile is currently in use by a segment and cannot be deleted.'
        }
    }

    const manualProfiles = await tenantGraphqlRequest(manualProfileByTenant, tenantId);
    const isProfileUsedInManualDial = manualProfiles.manualProfileByTenant.items.some(profile => profile.suppressionProfile === profileId);
    if (isProfileUsedInManualDial) {
        return {
            inUse: true,
            message: 'This suppression profile is currently in use by a manual dialing profile and cannot be deleted.'
        }
    }

    return { inUse: false };
}

export async function checkDNCListUsage(dncListId, tenantId) {
    const tenantSuppressionProfiles = await tenantGraphqlRequest(listSuppressions, tenantId);

    // Check if any of the tenant's suppression profiles are using the DNC list
    const isDNCListInUse = tenantSuppressionProfiles.listSuppressions.items.some(profile => {
        return profile.checks && JSON.parse(profile.checks).some(check => check.dncListSuppressions.dncs.includes(dncListId));
    });

    if (isDNCListInUse) {
        return {
            inUse: true,
            message: 'This DNC list is currently in use by a suppression profile and cannot be deleted.'
        }
    }

    return { inUse: false };
}

export async function checkContactFieldSuppresionListUsage(listId, tenantId) {
    const tenantSuppressionProfiles = await tenantGraphqlRequest(listSuppressions, tenantId);

    // Check if any of the tenant's suppression profiles are using the contact field suppression list
    const isListInUse = tenantSuppressionProfiles.listSuppressions.items.some(profile => {
        return profile.checks && JSON.parse(profile.checks).some(check => check.contactFieldSuppressions?.contactFieldSuppressions && JSON.parse(check.contactFieldSuppressions.contactFieldSuppressions).some(suppression => JSON.parse(suppression).id === listId));
    });

    if (isListInUse) {
        return {
            inUse: true,
            message: 'This contact field suppression list is currently in use by a suppression profile and cannot be deleted.'
        }
    }

    return { inUse: false };
}

/** Helper Functions */

async function getTenantCampaigns(tenantId) {
    const client = generateClient();
    const campaigns = await client.graphql({
        query: campaignsByTenant,
        variables: {
            tenant: tenantId,
            filter: {
                or: [{ status: { eq: 'Active' } }, { status: { eq: 'Paused' } }],
            },
        },
    });

    return campaigns.data.campaignsByTenant.items;
}

async function tenantGraphqlRequest(query, tenantId) {
    const client = generateClient();
    const response = await client.graphql({
        query,
        variables: {
            tenant: tenantId,
        },
    });

    return response.data;
}